import React, { useMemo } from 'react'
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { COLORS, FONTS, GLOW, GLASS } from '../shared/theme'
import { fadeIn, entrance, staggerDelay, breathe, pulse, SPRINGS } from '../shared/animations'
import SceneWrapper from '../shared/SceneWrapper'

/**
 * FlowDiagram — Diagrama de flujo con nodos y conectores SVG bezier animados.
 *
 * params: { steps: [{label, icon}], direction }
 */
export default function FlowDiagram({ params = {}, artDirection = {} }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const { steps = [], direction = 'horizontal' } = params

  const titleOpacity = fadeIn(frame, 0, 20)
  const isHorizontal = direction !== 'vertical'
  const count = Math.min(steps.length, 6)

  // Compute node positions for SVG connectors
  const nodePositions = useMemo(() => {
    if (isHorizontal) {
      const spacing = 220
      const totalW = (count - 1) * spacing
      const startX = (1920 - totalW) / 2
      return Array.from({ length: count }, (_, i) => ({
        x: startX + i * spacing,
        y: 480,
      }))
    }
    const spacing = 150
    const totalH = (count - 1) * spacing
    const startY = (1080 - totalH) / 2 + 40
    return Array.from({ length: count }, (_, i) => ({
      x: 960,
      y: startY + i * spacing,
    }))
  }, [count, isHorizontal])

  const nodeSize = count <= 4 ? 72 : 56

  return (
    <SceneWrapper accent={COLORS.accent.blue} texture={artDirection?.texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
        }}
      >
        {/* Title */}
        <div
          style={{
            position: 'absolute',
            top: 60,
            left: 0,
            right: 0,
            textAlign: 'center',
            opacity: titleOpacity,
            fontFamily: FONTS.heading,
            fontSize: 28,
            fontWeight: 600,
            color: COLORS.text.muted,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            zIndex: 5,
          }}
        >
          Flujo de proceso
        </div>

        {/* SVG connector layer */}
        <svg width={1920} height={1080} style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
          <defs>
            <filter id="flowGlow" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
            </filter>
          </defs>
          {nodePositions.slice(0, -1).map((pos, i) => {
            const next = nodePositions[i + 1]
            const delay = staggerDelay(i, 8) + 18
            const connOpacity = fadeIn(frame, delay, 12)
            const connPulse = pulse(frame, 0.06 + i * 0.01, 0.3, 0.8)

            // Bezier curve between nodes
            const cpOffset = isHorizontal ? (next.x - pos.x) * 0.4 : (next.y - pos.y) * 0.4
            const cp1x = isHorizontal ? pos.x + cpOffset : pos.x
            const cp1y = isHorizontal ? pos.y : pos.y + cpOffset
            const cp2x = isHorizontal ? next.x - cpOffset : next.x
            const cp2y = isHorizontal ? next.y : next.y - cpOffset
            const pathD = `M ${pos.x} ${pos.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y}`

            // Path draw animation
            const pathLen = Math.hypot(next.x - pos.x, next.y - pos.y) * 1.2
            const drawProgress = entrance(frame, fps, delay, SPRINGS.gentle)
            const dashOffset = interpolate(drawProgress, [0, 1], [pathLen, 0])

            // Traveling energy dot
            const dotT = (frame * 0.012 + i * 0.25) % 1
            const dotX =
              (1 - dotT) ** 3 * pos.x +
              3 * (1 - dotT) ** 2 * dotT * cp1x +
              3 * (1 - dotT) * dotT ** 2 * cp2x +
              dotT ** 3 * next.x
            const dotY =
              (1 - dotT) ** 3 * pos.y +
              3 * (1 - dotT) ** 2 * dotT * cp1y +
              3 * (1 - dotT) * dotT ** 2 * cp2y +
              dotT ** 3 * next.y

            return (
              <g key={`conn-${i}`} opacity={connOpacity}>
                {/* Glow path */}
                <path
                  d={pathD}
                  fill="none"
                  stroke={COLORS.accent.blue}
                  strokeWidth={6}
                  strokeDasharray={pathLen}
                  strokeDashoffset={dashOffset}
                  filter="url(#flowGlow)"
                  opacity={0.3 * connPulse}
                  strokeLinecap="round"
                />
                {/* Main connector */}
                <path
                  d={pathD}
                  fill="none"
                  stroke={COLORS.accent.blue}
                  strokeWidth={2}
                  strokeDasharray={pathLen}
                  strokeDashoffset={dashOffset}
                  strokeLinecap="round"
                  opacity={0.6}
                />
                {/* Energy dot */}
                {drawProgress > 0.5 && (
                  <>
                    <circle
                      cx={dotX}
                      cy={dotY}
                      r={4}
                      fill={COLORS.accent.teal}
                      opacity={connPulse * 0.9}
                    />
                    <circle
                      cx={dotX}
                      cy={dotY}
                      r={10}
                      fill={COLORS.accent.teal}
                      filter="url(#flowGlow)"
                      opacity={connPulse * 0.3}
                    />
                  </>
                )}
                {/* Arrow at end */}
                {drawProgress > 0.8 && (
                  <ArrowHead
                    x={next.x}
                    y={next.y}
                    fromX={cp2x}
                    fromY={cp2y}
                    color={COLORS.accent.blue}
                    size={nodeSize + 8}
                    opacity={fadeIn(frame, delay + 10, 8) * 0.6}
                  />
                )}
              </g>
            )
          })}
        </svg>

        {/* Node layer (HTML for glass effect) */}
        {steps.slice(0, count).map((step, i) => {
          const pos = nodePositions[i]
          const delay = staggerDelay(i, 8) + 10
          const stepOpacity = fadeIn(frame, delay, 15)
          const stepScale = entrance(frame, fps, delay, SPRINGS.snappy)
          const breath = breathe(frame, 0.025 + i * 0.004, 0.008)

          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: pos.x - nodeSize,
                top: pos.y - nodeSize,
                width: nodeSize * 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 14,
                opacity: stepOpacity,
                transform: `scale(${stepScale * breath})`,
                zIndex: 3,
              }}
            >
              {/* Node circle */}
              <div
                style={{
                  width: nodeSize * 2,
                  height: nodeSize * 2,
                  borderRadius: '50%',
                  ...GLASS.card,
                  border: `2px solid ${COLORS.accent.blue}35`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  boxShadow: GLOW.sm(COLORS.accent.blue),
                }}
              >
                {/* Step number badge */}
                <div
                  style={{
                    position: 'absolute',
                    top: -8,
                    right: -4,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: COLORS.accent.blue,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: FONTS.heading,
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#fff',
                    boxShadow: GLOW.sm(COLORS.accent.blue),
                  }}
                >
                  {i + 1}
                </div>
                {/* Icon */}
                <span style={{ fontSize: count <= 4 ? 42 : 32 }}>{step.icon || '●'}</span>
              </div>
              {/* Label */}
              <div
                style={{
                  fontFamily: FONTS.body,
                  fontSize: count <= 4 ? 16 : 13,
                  fontWeight: 600,
                  color: COLORS.text.secondary,
                  textAlign: 'center',
                  maxWidth: 150,
                  lineHeight: 1.3,
                }}
              >
                {step.label}
              </div>
            </div>
          )
        })}
      </div>
    </SceneWrapper>
  )
}

/* ── Arrow head pointing toward node ── */
function ArrowHead({ x, y, fromX, fromY, color, size, opacity }) {
  const angle = Math.atan2(y - fromY, x - fromX)
  const arrowLen = 10
  const arrowWidth = 5
  const tipX = x - Math.cos(angle) * (size * 0.52)
  const tipY = y - Math.sin(angle) * (size * 0.52)
  const lx = tipX - Math.cos(angle - 0.4) * arrowLen
  const ly = tipY - Math.sin(angle - 0.4) * arrowLen
  const rx = tipX - Math.cos(angle + 0.4) * arrowLen
  const ry = tipY - Math.sin(angle + 0.4) * arrowLen

  return (
    <polygon points={`${tipX},${tipY} ${lx},${ly} ${rx},${ry}`} fill={color} opacity={opacity} />
  )
}
